/**
 * ShopCartContext.jsx — localStorage-backed cart for the Eminence store
 * Iter 389 · Feb 15 2026
 *
 * Small, focused context. Just add/remove/qty/clear. Persists to
 * localStorage so a page refresh doesn't nuke the cart.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const KEY = "kl_shop_cart_v1";

const ShopCartContext = createContext(null);

export function ShopCartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.sku === product.sku);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...prev,
        {
          sku: product.sku,
          name: product.name,
          price_cad: product.price_cad,
          image_url: product.image_url,
          points_reward: (product.points_reward || 0) * (
            Number(product.points_multiplier || 1) > 1 &&
            (!product.points_bonus_ends || new Date(`${product.points_bonus_ends}T23:59:59`) >= new Date())
              ? Number(product.points_multiplier) : 1),
          qty,
        },
      ];
    });
    setDrawerOpen(true);
  };

  const setQty = (sku, qty) => {
    setItems((prev) =>
      prev
        .map((p) => (p.sku === sku ? { ...p, qty: Math.max(1, qty) } : p))
        .filter((p) => p.qty > 0)
    );
  };

  const remove = (sku) => setItems((prev) => prev.filter((p) => p.sku !== sku));
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price_cad * i.qty, 0);
    const count = items.reduce((s, i) => s + i.qty, 0);
    const points = items.reduce((s, i) => s + (i.points_reward || 0) * i.qty, 0);
    return { subtotal, count, points };
  }, [items]);

  const value = { items, add, remove, setQty, clear, totals, drawerOpen, setDrawerOpen };

  return <ShopCartContext.Provider value={value}>{children}</ShopCartContext.Provider>;
}

export function useShopCart() {
  const ctx = useContext(ShopCartContext);
  if (!ctx) {
    // Return safe defaults so components don't crash when outside provider
    return {
      items: [],
      add: () => {},
      remove: () => {},
      setQty: () => {},
      clear: () => {},
      totals: { subtotal: 0, count: 0, points: 0 },
      drawerOpen: false,
      setDrawerOpen: () => {},
    };
  }
  return ctx;
}
